import styled from '@emotion/styled';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';
import { useLocation, matchRoutes } from 'react-router-dom';

import AgentDetailsLeftNav from '../project/agent-details-left-nav';
import CandidateModelsLeftNav from '../project/candidate-models-left-nav';
import FunctionalModelsLeftNav from '../project/functional-models-left-nav';
import ModelDetailsLeftNav from '../project/model-details-left-nav';
import SettingsNav from '../settings/left-nav';
import UseCaseLeftNav from '../use-case/use-case-left-nav';

import AddProjectPolicyLeftNav from '~/client/components/add-project-policy/left-nav';
import AssessmentLeftNav from '~/client/components/assessment/left-nav';
import RiskOverTimeLeftNav from '~/client/components/assessment/risk-over-time/left-nav';
import {
  LeftNavDrawer,
  useLeftNavContext,
} from '~/client/components/base/left-nav';
import AssessmentReportLeftNav from '~/client/components/report-generator/left-nav-assessment';
import ExecutiveReportLeftNav from '~/client/components/report-generator/left-nav-executive';
import {
  dashboardRouteUtil,
  projectRouteUtil,
  assessmentRouteUtil,
  aiSystemPolicyRouteUtil,
  assessmentReportRouteUtil,
  projectReportRouteUtil,
  policyDetailsRoutesUtil,
  governanceRoutesUtil,
  assuranceLibraryRoutesUtil,
  adminRoutesUtil,
  projectMembersRouteUtil,
  policyVersionsRoutesUtil,
  settingsRoutesUtil,
  aiDashboardUtils,
  functionalModelsRouteUtil,
  candidateModelsDetailsRouteUtil,
  candidateAgentsDetailsRouteUtil,
  aiSystemBundleRouteUtil,
  aiSystemAssessmentBundleVersionsRouteUtil,
  aiSystemAssessmentRouteUtil,
  functionalModelAssessmentRouteUtil,
  functionalModelBundleRouteUtil,
  functionalModelAssessmentBundleVersionsRouteUtil,
  candidateModelAssessmentRouteUtil,
  candidateAgentAssessmentRouteUtil,
  candidateModelBundleRouteUtil,
  candidateModelAssessmentBundleVersionsRouteUtil,
  functionalModelsPolicyRouteUtil,
  candidateModelPolicyRouteUtil,
  aiSystemAssessmentReportRouteUtil,
  functionalModelAssessmentReportRouteUtil,
  candidateModelAssessmentReportRouteUtil,
  candidateAgentAssessmentBundleVersionsRouteUtil,
  candidateAgentBundleRouteUtil,
  orgAdminDashboardUtils,
  useCaseRouteUtil,
  vendorModelAssessmentRouteUtil,
  vendorAgentAssessmentRouteUtil,
  vendorModelBundleRouteUtil,
  vendorAgentBundleRouteUtil,
  vendorModelAssessmentBundleVersionsRouteUtil,
  vendorAgentAssessmentBundleVersionsRouteUtil,
  vendorModelAssessmentReportRouteUtil,
  vendorAgentAssessmentReportRouteUtil,
} from '~/client/routes';
import PolicyDetailNav from '~c/components/dashboard/governance/policies/left-nav';
import PreviousPoliciesNav from '~c/components/dashboard/governance/policies/previous-versions/left-nav';
import DashboardLeftNav from '~c/components/dashboard/left-nav';
import ProjectLeftNav from '~c/components/project/left-nav';

interface Props {
  className?: string;
}

const LeftNavImpl: React.FC<Props> = ({ className }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { jumpTo, setMobileOpen } = useLeftNavContext();
  const prevPathnameRef = React.useRef(location.pathname);

  // Close mobile drawer only when the route actually changes (e.g. after picking a report).
  // Do not close when context updates (e.g. opening the drawer), which would re-run this effect
  // if setMobileOpen were in the dependency array.
  React.useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      prevPathnameRef.current = location.pathname;
      if (isMobile) {
        setMobileOpen(false);
      }
    }
  }, [location.pathname, isMobile, setMobileOpen]);

  React.useEffect(() => {
    const pathList = [
      { path: `${dashboardRouteUtil.routePrefix}/*` },
      { path: `${assessmentRouteUtil.routePrefix}/*` },
      { path: `${aiSystemPolicyRouteUtil.routePrefix}/*` },
      { path: `${projectRouteUtil.routePrefix}/*` },
      { path: `${projectReportRouteUtil.routePrefix}/*` },
      { path: `${assessmentReportRouteUtil.routePrefix}/*` },
      { path: `${policyDetailsRoutesUtil.routePrefix}/*` },
      { path: `${governanceRoutesUtil.routePrefix}/*` },
      { path: `${assuranceLibraryRoutesUtil.routePrefix}/*` },
      { path: `${functionalModelsPolicyRouteUtil.routePrefix}/*` },
      { path: `${candidateModelPolicyRouteUtil.routePrefix}/*` },
      { path: `${aiSystemAssessmentRouteUtil.routePrefix}/*` },
      { path: `${aiSystemBundleRouteUtil.routePrefix}/*` },
      { path: `${adminRoutesUtil.routePrefix}/*` },
      { path: `${functionalModelAssessmentRouteUtil.routePrefix}/*` },
      { path: `${functionalModelBundleRouteUtil.routePrefix}/*` },
      { path: `${aiSystemAssessmentBundleVersionsRouteUtil.routePrefix}/*` },
      {
        path: `${functionalModelAssessmentBundleVersionsRouteUtil.routePrefix}/*`,
      },
      { path: `${candidateModelAssessmentRouteUtil.routePrefix}/*` },
      { path: `${candidateAgentAssessmentRouteUtil.routePrefix}/*` },
      { path: `${candidateModelBundleRouteUtil.routePrefix}/*` },
      {
        path: `${candidateModelAssessmentBundleVersionsRouteUtil.routePrefix}/*`,
      },
      { path: `${candidateAgentBundleRouteUtil.routePrefix}/*` },
      {
        path: `${candidateAgentAssessmentBundleVersionsRouteUtil.routePrefix}/*`,
      },
      { path: `${projectMembersRouteUtil.routePrefix}/*` },
      { path: `${policyVersionsRoutesUtil.routePrefix}/*` },
      { path: `${settingsRoutesUtil.routePrefix}/*` },
      { path: `${aiDashboardUtils.routePrefix}/*` },
      { path: `${orgAdminDashboardUtils.routePrefix}/*` },
      { path: `${functionalModelsRouteUtil.routePrefix}/*` },
      { path: `${candidateModelsDetailsRouteUtil.routePrefix}/*` },
      { path: `${candidateAgentsDetailsRouteUtil.routePrefix}/*` },
      { path: `${aiSystemAssessmentReportRouteUtil.routePrefix}/*` },
      { path: `${functionalModelAssessmentReportRouteUtil.routePrefix}/*` },
      { path: `${candidateModelAssessmentReportRouteUtil.routePrefix}/*` },
      { path: `${useCaseRouteUtil.routePrefix}/*` },
      { path: `${vendorModelAssessmentRouteUtil.routePrefix}/*` },
      { path: `${vendorAgentAssessmentRouteUtil.routePrefix}/*` },
      { path: `${vendorModelBundleRouteUtil.routePrefix}/*` },
      { path: `${vendorAgentBundleRouteUtil.routePrefix}/*` },
      { path: `${vendorModelAssessmentBundleVersionsRouteUtil.routePrefix}/*` },
      { path: `${vendorAgentAssessmentBundleVersionsRouteUtil.routePrefix}/*` },
      { path: `${vendorModelAssessmentReportRouteUtil.routePrefix}/*` },
      { path: `${vendorAgentAssessmentReportRouteUtil.routePrefix}/*` },
    ];
    const matches = matchRoutes(pathList, location);
    if (matches && matches.length > 0) {
      const { path } = matches[0].route;
      // path should be one of the routes in the pathList
      // remove the `/*` at the end of the path
      let leftNavRoute = path.slice(0, -2);

      // Redirect vendor reports to dashboard so it shows the full nav
      const isVendorModelReport = location.pathname.match(
        /^\/vendor-model\/[^/]+\/assessment\/[^/]+\/report/
      );
      const isVendorAgentReport = location.pathname.match(
        /^\/vendor-agent\/[^/]+\/assessment\/[^/]+\/report/
      );

      if (isVendorModelReport || isVendorAgentReport) {
        leftNavRoute = dashboardRouteUtil.routePrefix;
      }

      // Redirect org-admin routes to settings nav (handles them in the Organization section)
      if (leftNavRoute === orgAdminDashboardUtils.routePrefix) {
        leftNavRoute = settingsRoutesUtil.routePrefix;
      }

      // Defer update so it doesn't run during hydration and trigger "Suspense boundary received an update before it finished hydrating"
      React.startTransition(() => {
        jumpTo(leftNavRoute);
      });
    }
  }, [location, jumpTo]);

  return (
    <LeftNavDrawer
      className={className}
      initialRoute={dashboardRouteUtil.routePrefix}
    >
      <DashboardLeftNav />
      <AddProjectPolicyLeftNav />
      <FunctionalModelsLeftNav />
      <CandidateModelsLeftNav />
      <ModelDetailsLeftNav />
      <AgentDetailsLeftNav />
      <ProjectLeftNav />
      <AssessmentLeftNav />
      <AssessmentReportLeftNav />
      <ExecutiveReportLeftNav />
      <PolicyDetailNav />
      <PreviousPoliciesNav />
      <RiskOverTimeLeftNav />
      <SettingsNav />
      <UseCaseLeftNav />
    </LeftNavDrawer>
  );
};

const LeftNav = styled(LeftNavImpl)`
  @media print {
    display: none;
  }
`;

export default LeftNav;
